import { Fragment } from 'react';
import styled from 'styled-components';
import { OZ } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import H3 from 'UIComponents/elements/headings/H3';
import Loadable from 'UIComponents/decorators/Loadable';
import UICopyButton from 'UIComponents/button/UICopyButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UILogo from 'UIComponents/image/UILogo';
import UISection from 'UIComponents/section/UISection';
import UITag from 'UIComponents/tag/UITag';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import BigText from 'marketplace-ui-common/components/elements/BigText';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import SupportInfo from '../records/SupportInfo';
import SupportLinkBox from './SupportLinkBox';
import AssetProviderProfile from './AssetProviderProfile';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUPPORT_LINK_IMAGE_HEIGHT = 72;
const AsyncAssetSupportBusinessHours = Loadable({
  loader: () => import( /* webpackChunkName: 'asset-support-business-hours' */'./AssetSupportBusinessHours'),
  LoadingComponent: ({
    pastDelay
  }) => pastDelay ? /*#__PURE__*/_jsx(UILoadingSpinner, {
    size: "extra-small"
  }) : null,
  ErrorComponent: () => /*#__PURE__*/_jsx(AbstractTextElement, {
    use: "error",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-assets-core.details.support.businessHours.loadingError"
    })
  })
});
const SectionTitle = ({
  children
}) => /*#__PURE__*/_jsx(H3, {
  className: "m-bottom-3 is--heading-5",
  children: children
});
const StyledIcon = styled.div.withConfig({
  displayName: "AssetSupport__StyledIcon",
  componentId: "sc-1yrg09w-0"
})(["line-height:1;padding-top:5px;"]);
const defaultLanguageTagRenderer = langCode => /*#__PURE__*/_jsx(UITag, {
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `SharedI18nStrings.languageNames.${langCode}`
  })
}, `supported-language-${langCode}`);
export default function AssetSupport({
  chatUrl,
  facebookUrl,
  getAssetEventSelectorId = () => '',
  languageTagRenderer = defaultLanguageTagRenderer,
  onTrackInteraction,
  phone,
  provider,
  responsive,
  support = new SupportInfo({
    answers: [],
    hours: []
  }),
  supportEmail,
  supportLanguages,
  supportUrl
}) {
  const {
    description,
    name
  } = provider;
  const [phoneNumber, phoneExtension] = phone ? phone.split('-') : [];
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(AssetProviderProfile, {
        onTrackInteraction: onTrackInteraction,
        providerAvatarSize: 60,
        providerWebsiteAnalyticsId: getAssetEventSelectorId('SUPPORT-PROVIDER-WEBSITE'),
        provider: provider
      })
    }), /*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(BigText, {
        children: description
      })
    }), support && !!support.answers.length && /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.details.support.supportIncludes.title"
        })
      }), /*#__PURE__*/_jsx(UIList, {
        styled: true,
        children: support.answers.map((answerText, index) => /*#__PURE__*/_jsx(BigText, {
          children: answerText
        }, `support-answer-${index}`))
      })]
    }), support && !!support.responseTime && /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.details.support.responseTime.title"
        })
      }), /*#__PURE__*/_jsx(BigText, {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "marketplace-ui-assets-core.details.support.responseTime.content",
          options: {
            providerName: name,
            responseTime: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-assets-core.responseTime.${support.responseTime}`
            })
          }
        })
      })]
    }), support && !!support.hours.length && /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.details.support.businessHours.title"
        })
      }), /*#__PURE__*/_jsx(BigText, {
        children: /*#__PURE__*/_jsx(AsyncAssetSupportBusinessHours, {
          hours: support.hours,
          timezone: support.timezone
        })
      })]
    }), /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.details.support.supportLanguages.title"
        })
      }), /*#__PURE__*/_jsx(UIList, {
        inline: true,
        children: supportLanguages.map(item => languageTagRenderer(item))
      })]
    }), /*#__PURE__*/_jsx(MediaQuery, {
      minScreenSize: responsive ? ScreenSizes.MD : ScreenSizes.XS,
      children: /*#__PURE__*/_jsxs(UISection, {
        children: [/*#__PURE__*/_jsx(SectionTitle, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-assets-core.details.support.contact.title",
            options: {
              providerName: name
            }
          })
        }), /*#__PURE__*/_jsxs(UIFlex, {
          align: "stretch",
          wrap: "wrap",
          style: {
            marginLeft: -12,
            marginTop: -12
          },
          children: [supportUrl && /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getAssetEventSelectorId('SUPPORT-SUPPORT-WEBSITE'),
            href: supportUrl,
            illustrationName: "add-on-website",
            onClick: () => onTrackInteraction('click support website link'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-assets-core.details.support.contact.supportUrl"
            })
          }), /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getAssetEventSelectorId('SUPPORT-EMAIL'),
            Button: UICopyButton,
            buttonProps: {
              value: supportEmail
            },
            illustrationName: "successful-send",
            onClick: () => onTrackInteraction('click copy email address'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-assets-core.details.support.contact.supportEmail"
            })
          }), chatUrl && /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getAssetEventSelectorId('SUPPORT-CHAT'),
            href: chatUrl,
            illustrationName: "messages",
            onClick: () => onTrackInteraction('click live chat link'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-assets-core.details.support.contact.chatUrl"
            })
          }), facebookUrl && /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getAssetEventSelectorId('SUPPORT-FACEBOOK'),
            href: facebookUrl,
            onClick: () => onTrackInteraction('click facebook link'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-assets-core.details.support.contact.facebookUrl"
            }),
            children: /*#__PURE__*/_jsx(UILogo, {
              name: "facebook-messenger",
              height: SUPPORT_LINK_IMAGE_HEIGHT
            })
          }), phone && /*#__PURE__*/_jsx(SupportLinkBox, {
            href: `tel:${phoneNumber}${phoneExtension ? `p${phoneExtension}` : ''}`,
            textLabel: /*#__PURE__*/_jsx(FormattedPhoneNumber, {
              value: phoneNumber
              // @ts-expect-error I18n props are wrong for this component
              ,
              extension: phoneExtension
            }),
            children: /*#__PURE__*/_jsx(StyledIcon, {
              children: /*#__PURE__*/_jsx(UIIcon, {
                className: "p-top-2",
                color: OZ,
                name: "calling",
                size: "lg"
              })
            })
          })]
        })]
      })
    })]
  });
}